import type { List } from 'immutable';
import _merge from 'lodash/merge';
import type { SagaIterator } from 'redux-saga';
import { type ActionPattern, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import type { Account } from 'app-components/flux/accounts/accountsTypes';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsTransformers,
} from 'app-components/flux/institution-logins';
import type { InstitutionLogin, MfaResponse } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import { institutionsActions, institutionsSelectors, institutionsUtils } from 'app-components/flux/institutions';
import type { Institution } from 'app-components/flux/institutions/institutionsTypes';
import {
  crashReporterInterface,
  getLogger,
  httpClientInterface,
  intercomTracker,
  isMobile,
  requireEnvironmentConfig,
  tracker,
} from 'app-components/utils/core';

import * as actions from './accountDiscoveryActions';
import * as selectors from './accountDiscoverySelectors';
import * as transformers from './accountDiscoveryTransformers';
import * as types from './accountDiscoveryTypes';

const log = getLogger('data/AccountDiscovery/sagas.js');

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type HttpResponse<Data = unknown> = {
  data: Data;
};

type AccountDiscoveryMeta = actionHelpers.Meta & {
  mode?: string;
  scope?: string;
  businessId?: string;
};

type SagaAction<Payload> = {
  payload: Payload;
  meta: AccountDiscoveryMeta;
};

type FormField = {
  displayOrder?: string | number;
  key: string;
  encrypt?: boolean;
};

type RequestCredential = {
  key: string;
  value?: string;
  encrypted?: boolean;
  encryptionKeyId?: string;
};

type InstitutionLoginRequestBody = Omit<
  ReturnType<typeof transformers.transformInstitutionLoginToRequestData>,
  'credentials'
> & {
  credentials?: List<RequestCredential> | null;
};

type FormEncryption = {
  publicKey?: string;
  signature?: string;
  signatureAlgorithm?: string;
  algorithm?: string;
  keyId?: string;
};

type InstitutionChannelData = {
  authType?: string;
  formSignatureAlgorithm?: string;
  formSignature?: string;
  formFields: FormField[];
  formEncryption: FormEncryption;
};

type PollAggregator = {
  id?: string | null;
  aggStatus?: string;
  cpAggStatusCode?: string;
  cpAggStatusDetail?: string;
  channel?: string;
  cpId?: string | null;
  cpLoginId?: string | null;
  mfaChallenges?: unknown;
};

type PollResponseData = transformers.QcsAccountDiscoveryDataResponse & {
  id?: string;
  mode?: string;
  isProcessing?: boolean;
  existingInstitutionLoginId?: string;
  pollingReference?: string;
  correlationIds?: Record<string, string>;
  aggregators?: PollAggregator[];
};

const sendRequest = (method: HttpMethod, url: string, data?: unknown) => httpClientInterface.send(method, url, data);

const cpResponseMeta = (meta: AccountDiscoveryMeta): actionHelpers.Meta | undefined =>
  actionHelpers.cpMetaForResponseAction(meta) as actionHelpers.Meta | undefined;

const cpEmbeddedMeta = (meta: AccountDiscoveryMeta): actionHelpers.Meta | undefined =>
  actionHelpers.cpMetaForEmbeddedAction(meta) as actionHelpers.Meta | undefined;

const toResponseError = (error: unknown): types.ResponseError => error as types.ResponseError;

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return undefined;
  }
  return typeof error.message === 'string' ? error.message : undefined;
};

const toActionPattern = (actionCreator: { toString(): string }): ActionPattern =>
  actionCreator.toString() as ActionPattern;

const takeLatestSagaAction = <Payload>(pattern: ActionPattern, worker: (action: SagaAction<Payload>) => SagaIterator) =>
  takeLatest(pattern, function* typedSagaActionWorker(action: unknown): SagaIterator {
    yield call(worker, action as SagaAction<Payload>);
  });

const takeEverySagaAction = <Payload>(pattern: ActionPattern, worker: (action: SagaAction<Payload>) => SagaIterator) =>
  takeEvery(pattern, function* typedSagaActionWorker(action: unknown): SagaIterator {
    yield call(worker, action as SagaAction<Payload>);
  });

export function* accountDiscovery(action: SagaAction<{ institutionLogin: InstitutionLogin }>): SagaIterator {
  const {
    payload: { institutionLogin },
    meta,
  } = action;
  const { resolve, reject, scope } = meta;

  let resolvedInstitutionLogin = institutionLogin;
  let institutionLoginId = institutionLogin.id !== '0' ? institutionLogin.id : null;

  let requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins`;
  if (institutionLoginId) {
    requestUrl += `/${institutionLoginId}`;
  }
  let requestBody: InstitutionLoginRequestBody = transformers.transformInstitutionLoginToRequestData(
    institutionLogin,
  ) as InstitutionLoginRequestBody;

  let isSecure: boolean | number = true;
  const fiCredentialEncryption: boolean | undefined = yield select(
    featureFlagsSelectors.getFeatureFlag,
    'fiCredentialEncryption',
  );
  if (fiCredentialEncryption) {
    const institution: Institution | undefined = yield select(selectors.getInstitution, { scope: scope! });
    const channelData: InstitutionChannelData | undefined = yield select(selectors.getInstitutionChannelData, {
      scope: scope!,
    });
    if (channelData?.authType && channelData.authType === 'CREDENTIAL_AUTH') {
      try {
        // node-forge is large and only credential-auth connects need it, so load it here rather than at startup.
        const forge: typeof import('node-forge') = yield call(() => import('node-forge'));
        // get public key: dig -t TXT services-bld.quickencs.com +short
        assert(requireEnvironmentConfig().signature_public_key, 'public key for signature not found #encrypt');
        const signaturePublicKey = forge.pki.publicKeyFromPem(
          `-----BEGIN PUBLIC KEY-----${requireEnvironmentConfig().signature_public_key}-----END PUBLIC KEY-----`,
        );
        // verify form signature 'displayOrder:cpId:encrypt;displayOrder:cpId:encrypt'
        if (
          assert(channelData, 'can not verify form signature: aggregator not found #encrypt') &&
          assert(
            channelData.formSignatureAlgorithm === 'SHA256withRSA',
            'unexpected form signature algorithm #encrypt',
          ) &&
          assert(signaturePublicKey, 'public key for form signature not found #encrypt')
        ) {
          const formData = channelData.formFields
            .map((formField: FormField) => `${formField.displayOrder}:${formField.key}:${formField.encrypt}`)
            .join(';');
          const dataToVerify = forge.md.sha256.create().update(formData, 'utf8');
          try {
            const verify = signaturePublicKey.verify(
              dataToVerify.digest().bytes(),
              forge.util.decode64(channelData.formSignature!),
            );
            assert(verify, 'form signature verification failed #encrypt');
            isSecure = Number(isSecure) & Number(verify);
          } catch (error) {
            const encryptionErrorMessage = getErrorMessage(error);
            // Handle `Encryption block is invalid.` and `Encrypted message is invalid.`
            // This handling is done for mobile as `node-forge` on RN apps fail
            // to verify encryption public key digest for the first app launch.
            // This is appilcable to mobile clients only.
            if (
              isMobile() &&
              (encryptionErrorMessage === 'Encryption block is invalid.' ||
                encryptionErrorMessage === 'Encrypted message is invalid.')
            ) {
              // Do nothing, continue.
            } else {
              log.error(error);
              throw error;
            }
          }
        } else {
          isSecure = false;
        }

        // verify encryption key signature
        const { formEncryption } = channelData;
        if (
          assert(
            formEncryption.signatureAlgorithm === 'SHA256withRSA',
            'unexpected encryption signature algorithm #encrypt',
          ) &&
          assert(signaturePublicKey, 'public key for signature not found #encrypt')
        ) {
          const dataToVerify = forge.md.sha256.create().update(formEncryption.publicKey!, 'utf8');
          try {
            const verify = signaturePublicKey.verify(
              dataToVerify.digest().bytes(),
              forge.util.decode64(formEncryption.signature!),
            );
            assert(verify, 'encryption key signature verification failed #encrypt');
            isSecure = Number(isSecure) & Number(verify);
          } catch (error) {
            const encryptionErrorMessage = getErrorMessage(error);
            // Handle `Encryption block is invalid.` and `Encrypted message is invalid.`
            // This handling is done for mobile as `node-forge` on RN apps fail
            // to verify encryption public key digest for the first app launch.
            // This is appilcable to mobile clients only.
            if (
              isMobile() &&
              (encryptionErrorMessage === 'Encryption block is invalid.' ||
                encryptionErrorMessage === 'Encrypted message is invalid.')
            ) {
              // Do nothing, continue.
            } else {
              log.error(error);
              throw error;
            }
          }
        } else {
          isSecure = false;
        }

        if (
          assert(
            formEncryption?.algorithm === 'RSA',
            `unexpected encryption algorithm "${formEncryption?.algorithm}" #encrypt`,
          ) &&
          assert(formEncryption?.publicKey, 'encryption key not found #encrypt')
        ) {
          requestBody.credentials = requestBody.credentials?.map((field) => {
            const institutionFormField = channelData.formFields.find(
              (formField: FormField) => formField.key === field.key,
            );
            if (institutionFormField?.encrypt && field.value != null) {
              const pem = `-----BEGIN PUBLIC KEY-----${formEncryption.publicKey}-----END PUBLIC KEY-----`;
              const publicKey = forge.pki.publicKeyFromPem(pem);
              const encryptedValue = publicKey.encrypt(forge.util.encodeUtf8(field.value));
              const value = btoa(encryptedValue);
              return {
                ...field,
                encrypted: true,
                encryptionKeyId: formEncryption.keyId,
                value,
              };
            }
            return field;
          }) as List<RequestCredential> | null;
        } else {
          isSecure = false;
        }
      } catch (error) {
        assert(false, `${String(error)} #encrypt`);
        crashReporterInterface.reportError(error, {
          custom: {
            institutionId: institutionLogin.institutionId,
            aggregator: institution?.aggregator,
          },
        });
        isSecure = false;
      }
    }
    if (!isSecure) {
      crashReporterInterface.reportError(Error('encryption failed (details)'), {
        custom: {
          institutionId: institutionLogin.institutionId,
          aggregator: institution?.aggregator,
        },
      });

      const error = new types.AccountDiscoveryFailure(
        'Unable to encrypt credentials',
        'AD_UNABLE_TO_ENCRYPT_CREDENTIALS',
        'vs.100',
      );
      yield put(
        actions.accountDiscoveryError({ error, institutionLogin: resolvedInstitutionLogin }, cpResponseMeta(meta)),
      );
      yield call(reject!, { error });
      const aggregator = institutionsUtils.getAggregatorFromChannel(
        (institutionLogin.fiMigrationInfo?.toChannel || institutionLogin.channel) as string,
      );
      yield put(
        institutionsActions.getInstitution(
          { id: institutionLogin.institutionId as string | undefined },
          cpEmbeddedMeta(_merge(meta, { axiosConfig: { params: { aggregator } } })),
        ),
      );
      return;
    }
  }

  try {
    const httpMethod = institutionLoginId ? 'PUT' : 'POST';
    const response: HttpResponse<PollResponseData> = yield call(sendRequest, httpMethod, requestUrl, requestBody);
    log.log('Upserted institition-login, response=', response.data);

    if (!institutionLoginId) {
      institutionLoginId = response.data.id as string;
      resolvedInstitutionLogin = resolvedInstitutionLogin.set('id', institutionLoginId);
    }
  } catch (exceptionError) {
    const firstError = toResponseError(exceptionError);
    log.log('Received error', exceptionError, firstError.response);
    let error: types.ResponseError | null = firstError;
    if (error.response) {
      if (
        !institutionLoginId &&
        error.response.status === 409 &&
        error.response.data?.errors?.[0]?.extData?.attributeName === 'Credentials'
      ) {
        log.debug('Received 409 response, turning request into update');

        // Server detected that we are creating a login in which we already have one for the same username; so,
        // until server does it, we will redo requesting after changing to an update instead of create
        try {
          institutionLoginId = error.response.data.errors[0].extData?.conflictId || null;
          const conflictingInstitutionLogin: InstitutionLogin = yield select(
            institutionLoginsSelectors.getInstitutionLoginForId,
            institutionLoginId!,
          );

          // here we are swapping out institutionLogin to the conflicting institutionLogin
          //
          resolvedInstitutionLogin = conflictingInstitutionLogin.merge({
            credentials: institutionLogin.credentials,
            cpSetupMode: institutionLogin.cpSetupMode,
          });

          requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins`;
          requestUrl += `/${institutionLoginId}`;
          requestBody = transformers.transformInstitutionLoginToRequestData(
            resolvedInstitutionLogin,
          ) as InstitutionLoginRequestBody;

          const response: HttpResponse<PollResponseData> = yield call(sendRequest, 'PUT', requestUrl, requestBody);
          log.log('Upserted conflicting institution-login, response=', response.data);

          // clear error since put was sucessful
          error = null;
        } catch (exceptionError2) {
          error = toResponseError(exceptionError2);
        }
      }
    }

    if (error) {
      log.log('Error upserting institution-login, error=', error);
      const actionMetaError = cpResponseMeta(meta);
      yield put(actions.accountDiscoveryCredentialsFormSubmitResponse(error, actionMetaError));
      //  yield put(institutionLoginsActions.credentialsFormSubmitSuccess(error, actionMetaError));

      yield put(
        actions.accountDiscoveryError({ error, institutionLogin: resolvedInstitutionLogin }, cpResponseMeta(meta)),
      );
      yield call(reject!, { institutionLogin: resolvedInstitutionLogin, error });

      // if in error, don't continue
      return;
    }
  }

  const actionMeta = cpResponseMeta(meta);
  yield put(
    actions.accountDiscoveryCredentialsFormSubmitResponse({ institutionLogin: resolvedInstitutionLogin }, actionMeta),
  );
  yield put(
    institutionLoginsActions.credentialsFormSubmitSuccess({ institutionLogin: resolvedInstitutionLogin }, actionMeta),
  );
  yield call(resolve!, resolvedInstitutionLogin);
  yield put(institutionLoginsActions.getInstitutionLogins());

  // make flow happy
  institutionLoginId = institutionLoginId || '0';

  // poll until institution-login is ready or cancel action
  requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/${institutionLoginId}/poll`;
  yield call(poll, requestUrl, institutionLoginId, meta);
}

export function* submitMfaResponses({
  payload: { mfaResponse },
  meta,
}: SagaAction<{ mfaResponse: MfaResponse }>): SagaIterator {
  log.log('Submitting MFA Response for Account Discovery', mfaResponse);
  const { resolve, reject } = meta;

  let requestUrl = `${requireEnvironmentConfig().services_url}${mfaResponse.submitUrl}`;
  const requestBody = {
    institutionLoginId: mfaResponse.institutionLoginId,
    mfaResponses: mfaResponse.answers.map((answer: { id: string; value: string }) => ({
      key: answer.id,
      response: answer.value,
    })),
  };

  try {
    yield call(sendRequest, 'POST', requestUrl, requestBody);

    yield put(actions.accountDiscoveryMfaFormSubmitResponse({}, cpResponseMeta(meta)));
    yield call(resolve!, mfaResponse.institutionLoginId);
  } catch (error) {
    log.error('Received error', error);

    yield put(actions.accountDiscoveryMfaFormSubmitResponse(toResponseError(error), cpResponseMeta(meta)));
    yield call(reject!, { error });

    // if in error, don't continue
    return;
  }

  // poll until institution-login is ready or cancel action
  requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/${mfaResponse.institutionLoginId}/poll`;
  yield call(poll, requestUrl, mfaResponse.institutionLoginId, meta);
}

function* poll(url: string, institutionLoginId: string, meta: AccountDiscoveryMeta): SagaIterator {
  let response: HttpResponse<PollResponseData>;
  const refreshPollTimeout: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollTimeout')) || 300000;
  const refreshPollInterval: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollInterval')) || 3000;
  let timeout = refreshPollTimeout / refreshPollInterval;
  let dataSnapshot: PollResponseData | undefined;
  tracker.timeEvent(tracker.events.refreshPoll);
  while (timeout > 0) {
    try {
      log.log(`Number of times left to poll is ${timeout} #account-discovery`);

      response = yield call(sendRequest, 'GET', url);
      log.debug('Polling upserted institition-logins, response=', response.data);

      const adStatus = transformers.transformResponseToAccountDiscoveryStatus(
        response as Parameters<typeof transformers.transformResponseToAccountDiscoveryStatus>[0],
      );
      yield put(actions.accountDiscoveryStatus({ status: adStatus }, cpResponseMeta(meta)));

      const { data } = response;
      dataSnapshot = response.data;
      if (data.status === 'ACTION_REQUIRED') {
        const { aggregators = [] } = response.data;
        if (aggregators.length !== 1) {
          throw new Error('Unsupported number of aggregators when extracting MFA Challenge');
        }
        const aggregator = aggregators[0];
        if (aggregator.aggStatus !== 'MFA_CHALLENGE_READY_FOR_USER') {
          throw new Error(
            'Trying to transform response to MFA Challenge when aggStatus != MFA_CHALLENGE_READY_FOR_USER',
          );
        }

        // todo: this should really come from QCS
        const submitUrl = `/institution-logins/${data.institutionLoginId}/mfa-response`;

        const mfaChallenge = institutionLoginsTransformers.transformMfaResponseToMfaChallenge(
          data.institutionLoginId!,
          aggregator.mfaChallenges as institutionLoginsTransformers.QCSChallengeItem[],
          submitUrl,
        );
        yield put(actions.accountDiscoveryMfaRequired({ mfaChallenge }, cpResponseMeta(meta)));
      }

      if (!data.isProcessing) {
        // if QCS detects we are attempting to modify existing login, switch it to the one QCS detected
        if (data.existingInstitutionLoginId) {
          // Biome is mad about unused suppression. These rules will turn back on eventually and we don't wan to lose this
          /// biome-ignore supressions/unused lint/style/noParameterAssign: Super old code we don't want to touch
          institutionLoginId = data.existingInstitutionLoginId;
        }
        const existingInstitutionLogin: InstitutionLogin = yield select(
          institutionLoginsSelectors.getInstitutionLoginForId,
          institutionLoginId!,
        );
        assert(existingInstitutionLogin, 'institution login with id not found', { loginId: institutionLoginId });
        if (data.status === 'OK') {
          if (data.mode === 'UPDATE_CREDENTIALS') {
            yield put(
              actions.accountDiscoverySuccess({ institutionLogin: existingInstitutionLogin }, cpResponseMeta(meta)),
            );
          } else {
            const allAccountsById = yield select(accountsSelectors.getAccountsById);
            const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(data, allAccountsById);
            yield put(
              actions.accountDiscoverySuccessWithAccounts(
                { institutionLogin: existingInstitutionLogin, accountDiscoveryData },
                cpResponseMeta(meta),
              ),
            );
          }
        } else if (data.status !== 'ACTION_REQUIRED') {
          log.log('Status is in another terminated state', data.status);
          const institution: Institution | undefined = yield select(
            institutionsSelectors.getInstitution,
            existingInstitutionLogin.institutionId!,
          );
          // poll termination due to error
          data.aggregators?.forEach?.((aggregator: PollAggregator) => {
            // track for intercom
            intercomTracker?.logEvent('addFIError', {
              code: aggregator.cpAggStatusCode,
              aggStatus: aggregator.aggStatus,
              details: aggregator.cpAggStatusDetail,
              channel: aggregator.channel,
            });
            tracker.track(tracker.events.addFIError, {
              mode: meta?.mode,
              error_status: aggregator.aggStatus,
              error_code: aggregator.cpAggStatusCode,
              agg_status: aggregator.aggStatus,
              agg_status_code: aggregator.cpAggStatusCode,
              agg_status_detail: aggregator.cpAggStatusDetail,
              channel: aggregator.channel,
              institution_login_channel: aggregator.channel,
              aggregator: institution?.aggregator,
              institution_id: institution?.id,
              institution_name: institution?.name,
            });
          });
          yield put(
            actions.accountDiscoveryFailure(
              { adStatus, institutionLogin: existingInstitutionLogin },
              cpResponseMeta(meta),
            ),
          );
        } else {
          const institution: Institution | undefined = yield select(
            institutionsSelectors.getInstitution,
            existingInstitutionLogin.institutionId!,
          );
          // poll termination due to action required
          data.aggregators?.forEach?.((aggregator: PollAggregator) => {
            // track for intercom
            intercomTracker?.logEvent('addFIError', {
              code: aggregator.cpAggStatusCode,
              aggStatus: aggregator.aggStatus,
              details: aggregator.cpAggStatusDetail,
              channel: aggregator.channel,
            });
            tracker.track(tracker.events.addFIError, {
              mode: meta?.mode,
              error_status: aggregator.aggStatus,
              error_code: aggregator.cpAggStatusCode,
              agg_status: aggregator.aggStatus,
              agg_status_code: aggregator.cpAggStatusCode,
              agg_status_detail: aggregator.cpAggStatusDetail,
              channel: aggregator.channel,
              institution_login_channel: aggregator.channel,
              aggregator: institution?.aggregator,
              institution_id: institution?.id,
              institution_name: institution?.name,
            });
          });
          yield put(
            actions.accountDiscoveryStopped(
              { adStatus, institutionLogin: existingInstitutionLogin },
              cpResponseMeta(meta),
            ),
          );
        }

        tracker.track(tracker.events.refreshPoll, { tag: 'poll', status: 'success' });

        // server is done processing so stop polling
        return;
      }

      timeout -= 1;
      yield delay(refreshPollInterval);
    } catch (error) {
      const pollError = toResponseError(error);
      // uncaught or network error
      assert('Account Discovery Error', error);
      tracker.track(tracker.events.refreshPoll, { tag: 'poll', status: 'error' });
      intercomTracker?.logEvent('addFIError', {
        mode: meta?.mode,
        code: pollError.statusCode,
        aggStatus: pollError.status,
        details: pollError.message,
        channel: dataSnapshot?.aggregators?.find((agg: PollAggregator) => agg?.channel)?.channel || 'unknown',
      });
      const institutionLogin: InstitutionLogin | undefined = yield select(
        institutionLoginsSelectors.getInstitutionLoginForId,
        institutionLoginId,
      );
      const institution: Institution | undefined = yield select(
        institutionsSelectors.getInstitution,
        institutionLogin?.institutionId as string,
      );
      tracker.track(tracker.events.addFIError, {
        error_status: pollError.message,
        error_code: pollError.code,
        channel: institutionLogin?.channel,
        institution_login_channel: institutionLogin?.channel,
        aggregator: institution?.aggregator,
        institution_id: institution?.id,
        institution_name: institution?.name,
      });
      // TODO: I believe this may cause an exception, institionLogin likely doesn't exist in our Redux store for new Add-FI
      yield put(actions.accountDiscoveryError({ error: pollError, institutionLogin }, cpResponseMeta(meta)));
      return;
    }
  }

  // time-out
  log.log('Time-out #account-discovery');
  const institutionLogin: InstitutionLogin | undefined = yield select(
    institutionLoginsSelectors.getInstitutionLoginForId,
    institutionLoginId,
  );
  const error = new types.AccountDiscoveryFailure('Account Discovery Timeout', 'AD_TIMEOUT', 'to.100');
  yield put(actions.accountDiscoveryError({ error, institutionLogin }, cpResponseMeta(meta)));

  tracker.track(tracker.events.refreshPoll, { tag: 'poll', status: 'timeout' });
  intercomTracker?.logEvent('addFIError', {
    code: '408',
    aggStatus: 'TIMEOUT',
    details: 'Timeout',
    channel: dataSnapshot?.aggregators?.find((agg: PollAggregator) => agg?.channel)?.channel || 'unknown',
  });
  const institution: Institution | undefined = yield select(
    institutionsSelectors.getInstitution,
    institutionLogin?.institutionId as string,
  );
  tracker.track(tracker.events.addFIError, {
    mode: meta?.mode,
    error_status: 'Account Discovery Timeout',
    error_code: 'AD_TIMEOUT',
    channel: institutionLogin?.channel,
    institution_login_channel: institutionLogin?.channel,
    aggregator: institution?.aggregator,
    institution_id: institution?.id,
    institution_name: institution?.name,
  });
}

// =================================================================================================
// getPartnerAuthUri
// =================================================================================================

export function* getPartnerAuthUris({
  payload,
  meta,
}: SagaAction<{
  partnerAuth: { intuitProperty: string; partnerUid: string };
  redirectUri: string;
}>): SagaIterator {
  log.debug('getPartnerAuthUris called...', payload);

  const { partnerAuth, redirectUri } = payload;
  const { intuitProperty, partnerUid } = partnerAuth;

  const requestUrl = `${
    requireEnvironmentConfig().services_url
  }/intuit-partnerauth?intuit_property=${intuitProperty}&partner_uid=${partnerUid}&offering_redirect_uri=${redirectUri}`;

  try {
    const response: HttpResponse<{ uris: string[] }> = yield call(sendRequest, 'GET', requestUrl);
    log.debug('Retrieved partnerAuthUris, response=', response);

    const partnerAuthUris = transformers.transformResponseToPartnerAuthUris(response);
    log.debug('getPartnerAuthUris response after transform=', partnerAuthUris);

    yield put(actions.getPartnerAuthUrisSuccess({ partnerAuthUris }, cpResponseMeta(meta)));
    meta.resolve?.({ partnerAuthUris });
  } catch (error) {
    log.debug('Error retrieving partnerAuthUris', error);
    yield put(actions.getPartnerAuthUrisFailure({ error: toResponseError(error) }, cpResponseMeta(meta)));
    meta.reject?.(error);
  }
}

function transformAddInstitutionLoginAndAccountsPayloadWithResponse(
  institutionLogin: InstitutionLogin,
  accounts: Account[],
  response: HttpResponse<PollResponseData>,
): [InstitutionLogin, Account[]] {
  const updatedInstitutionLogin = institutionLogin.set('id', response.data.id as string);

  const { correlationIds } = response.data;
  const updatedAccounts = accounts.map((account: Account) => {
    if (!correlationIds) {
      throw new Error('Missing correlationIds in addInstitutionLoginAndAccounts response');
    }
    const accountId = correlationIds[account.clientId ?? ''];
    return account.merge({
      id: accountId || account.id,
      institutionLoginId: updatedInstitutionLogin.id ?? undefined,
      isDeleted: account.isDeleted || account.isIgnored,
    });
  });
  return [updatedInstitutionLogin, updatedAccounts];
}

// =================================================================================================
// addInstitutionLoginAndAccounts
// =================================================================================================
export function* addInstitutionLoginAndAccounts({
  payload: { institutionLogin, accounts },
  meta,
}: SagaAction<{ institutionLogin: InstitutionLogin; accounts: Account[] }>): SagaIterator {
  log.log('Upserting InstitutionLogin with accounts', institutionLogin, accounts);
  let institutionLoginId = institutionLogin.id;
  let correlationIds: Record<string, string> | undefined;

  let requestMethod: HttpMethod = 'POST';
  let requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins`;
  if (institutionLoginId) {
    requestMethod = 'PUT';
    requestUrl += `/${institutionLoginId}`;
  }
  const requestBody = transformers.transformInstitutionLoginToRequestData(institutionLogin, accounts);

  let pollingUrl: string | null = null;
  try {
    const response: HttpResponse<PollResponseData> = yield call(sendRequest, requestMethod, requestUrl, requestBody);
    log.debug('Created institition-login and added accounts, response=', response.data);

    const [updatedInstitutionLogin, updatedAccounts] = transformAddInstitutionLoginAndAccountsPayloadWithResponse(
      institutionLogin,
      accounts,
      response,
    );
    log.debug('Transformed values', updatedInstitutionLogin, updatedAccounts);

    institutionLoginId = updatedInstitutionLogin.id;
    correlationIds = response.data.correlationIds;
    pollingUrl = response.data.pollingReference
      ? `${requireEnvironmentConfig().services_url}${response.data.pollingReference}`
      : null;
    yield put(
      institutionLoginsActions.addInstitutionLoginAndAccountsResponse(
        { institutionLogin: updatedInstitutionLogin },
        cpResponseMeta(meta),
      ),
    );
    yield put(accountsActions.applyAccountsChanges(updatedAccounts));
  } catch (error) {
    log.log('Error adding institution-login and accounts, error=', error);
    yield put(
      institutionLoginsActions.addInstitutionLoginAndAccountsResponse(
        // The creator's payload type doesn't model the failure case yet (institution-logins
        // is not converted); the reducers narrow on the payload shape at runtime.
        error as { institutionLogin: InstitutionLogin },
        cpResponseMeta(meta),
      ),
    );
    meta.reject?.({ id: institutionLoginId });

    // if in error, don't continue
    return;
  }
  meta.resolve?.({ id: institutionLoginId, correlationIds });
  if (pollingUrl) {
    yield call(doAddInstitutionLoginAndAccountsPolling, pollingUrl, institutionLoginId, meta);
  }
}

function* doAddInstitutionLoginAndAccountsPolling(
  url: string,
  institutionLoginId: string,
  meta: AccountDiscoveryMeta,
): SagaIterator {
  const refreshPollTimeout: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollTimeout')) || 300000;
  const refreshPollInterval: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollInterval')) || 3000;
  let numTimesLeftToPoll = refreshPollTimeout / refreshPollInterval;
  tracker.timeEvent(tracker.events.refreshPoll);
  while (numTimesLeftToPoll > 0) {
    try {
      const response: HttpResponse<
        Parameters<typeof institutionLoginsTransformers.transformResponseToRefreshStatus>[0]
      > = yield call(sendRequest, 'GET', url);
      log.log('Polling for refresh status', response.data);

      const refreshStatus = institutionLoginsTransformers.transformResponseToRefreshStatus(response.data);
      log.log('Status after transforming...', refreshStatus);

      // send refresh status
      yield put(institutionLoginsActions.refreshAccountsStatuses(refreshStatus));

      if (!response.data.isProcessing) {
        yield put(
          institutionLoginsActions.refreshAccountsCompleted({
            institutionLoginId,
            status: 'DONE_PROCESSING_WITH_UPDATE',
          }),
        );

        // since we are done processing, stop polling
        break;
      }

      numTimesLeftToPoll -= 1;
      log.log(`Number of times left to poll is ${numTimesLeftToPoll} #add-account`);
      yield delay(refreshPollInterval);
    } catch (error) {
      log.log('How did we get here...', error);
      tracker.track(tracker.events.refreshPoll, { tag: 'doAddInstitutionLoginAndAccountsPolling', status: 'error' });
      yield put(
        institutionLoginsActions.refreshAccountsCompleted(
          { institutionLoginId, status: 'FAILED_WHEN_POLLING' },
          cpResponseMeta(meta),
        ),
      );
      break;
    }
  }

  if (numTimesLeftToPoll === 0) {
    tracker.track(tracker.events.refreshPoll, { tag: 'doAddInstitutionLoginAndAccountsPolling', status: 'timeout' });
    yield put(
      institutionLoginsActions.refreshAccountsCompleted(
        { institutionLoginId, status: 'TIMEOUT_WHILE_PROCESSING' },
        cpResponseMeta(meta),
      ),
    );
  } else {
    tracker.track(tracker.events.refreshPoll, { tag: 'doAddInstitutionLoginAndAccountsPolling', status: 'success' });
  }
}

// =================================================================================================
// getAggregationInfo
//
// Retrieves aggregation-info required for some aggregators. For example,
//   - Finicity
//     - Requires Connect URI
//     - Requires Polling Reference Used to Retrieve Discovered Accounts
//   - Plaid
//     - Requires Plaid Link Token
// =================================================================================================
export function* getAggregationInfo({
  payload: { channel, institution, institutionLogin },
  meta,
}: SagaAction<types.AggregationInfoRequest>): SagaIterator {
  log.debug('getAggregationInfo called...', channel, institution, institutionLogin);
  const { resolve, reject } = meta;

  const requestUrl = new URL(`${requireEnvironmentConfig().services_url}/institutions/aggregation-info`);
  if (channel) {
    requestUrl.searchParams.set('channel', channel);
  }
  if (institution) {
    requestUrl.searchParams.set('institutionId', institution.id);
  }
  if (institutionLogin?.id) {
    requestUrl.searchParams.set('institutionLoginId', institutionLogin.id);
  }
  if (meta.axiosConfig?.params) {
    Object.entries(meta.axiosConfig.params).forEach(([key, value]) => {
      requestUrl.searchParams.set(key, String(value));
    });
  }

  try {
    const response: Parameters<typeof transformers.transformResponseToAggregationInfo>[0] = yield call(
      sendRequest,
      'GET',
      requestUrl.toString(),
    );
    const transformedResponse = transformers.transformResponseToAggregationInfo(response);
    log.debug('Retrieved aggregationInfo', transformedResponse, response);

    yield call(resolve!, transformedResponse);
  } catch (error) {
    log.debug('Error retrieving aggregation info', error);
    yield call(reject!, error);
  }
}

// =================================================================================================
// discoverInstitutionLoginWithAccounts
//
// This is used to build a discover response from an institution login and accounts.
// =================================================================================================
export function* discoverInstitutionLoginWithAccounts({
  payload: { institutionLogin, accounts },
  meta,
}: SagaAction<{ institutionLogin: types.DiscoveryInstitutionLogin; accounts?: Account[] }>): SagaIterator {
  log.log('Discovering institition-login with accounts', institutionLogin, accounts);
  const { resolve, reject } = meta;

  const requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/discover`;
  const requestBody = transformers.transformInstitutionLoginToRequestData(institutionLogin, accounts);

  try {
    const response: HttpResponse<PollResponseData> = yield call(sendRequest, 'POST', requestUrl, requestBody);

    // Based on the QCS response, following are the possible scenarios related to the institution-login:
    //   - Case 1: Performing an add-FI. No institution-login ID in payload and response.
    //     - QCS performed discovery, no institutionLogin created/exists. Client will need to add one when adding
    //       accounts.
    //   - Case 2: Performing an add-FI. No institution-login ID in payload but QCS returns an institution-login ID.
    //     - QCS created a new institutionLogin as part of discovery. Client just needs to add/update accounts.
    //   - Case 3: Performing an add/update FI. QCS returns an existingInstitutionLoginId which is different from the
    //     institutionLogin.id in payload.
    //
    let institutionLoginToUpsert: types.DiscoveryInstitutionLogin | null | undefined = null;
    if (response.data.existingInstitutionLoginId && response.data.existingInstitutionLoginId !== institutionLogin.id) {
      // Case 3
      institutionLoginToUpsert = yield select(
        institutionLoginsSelectors.getInstitutionLoginForId,
        response.data.existingInstitutionLoginId,
      );
    } else if (!institutionLogin.id && response.data.institutionLoginId) {
      // Case 2
      // The two login types differ only in id, so the login is an InstitutionLogin once it has one.
      const createdInstitutionLogin = institutionLogin.set(
        'id',
        response.data.institutionLoginId as string,
      ) as InstitutionLogin;
      institutionLoginToUpsert = createdInstitutionLogin;
      yield put(institutionLoginsActions.updateChangedInstitutionLogins([createdInstitutionLogin]));
    } else {
      // Case 1
      institutionLoginToUpsert = institutionLogin;
    }

    const allAccountsById = yield select(accountsSelectors.getAccountsById);
    const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(
      response.data,
      allAccountsById,
      meta,
    );
    log.debug('Transformed values', institutionLoginToUpsert, accountDiscoveryData);

    yield call(resolve!, { institutionLogin: institutionLoginToUpsert, accountDiscoveryData });
  } catch (error) {
    log.debug('Error discovering institution-login with accounts, error=', error);
    yield call(reject!, error);
  }
}

// =================================================================================================
// discoverInstitutionLoginWithAccountsByPolling
//
// This is used to build a discover response from an institution login and accounts.
// =================================================================================================
export function* discoverInstitutionLoginWithAccountsByPolling({
  payload: { institutionLogin, pollingRef },
  meta,
}: SagaAction<{ institutionLogin: types.DiscoveryInstitutionLogin; pollingRef: string }>): SagaIterator {
  log.log('Discovering institition-login with accounts by polling', institutionLogin, pollingRef);
  const { resolve, reject } = meta;

  const url = `${requireEnvironmentConfig().services_url}${pollingRef}`;

  const refreshPollTimeout: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollTimeout')) || 300000;
  const refreshPollInterval: number =
    (yield select(featureFlagsSelectors.getFeatureFlag, 'refreshPollInterval')) || 3000;
  let timeout = refreshPollTimeout / refreshPollInterval;
  tracker.timeEvent(tracker.events.refreshPoll);
  while (timeout > 0) {
    try {
      const response: HttpResponse<PollResponseData> = yield call(sendRequest, 'GET', url);
      log.log('Polling for discovered accounts', response.data);

      const { data } = response;
      if (!data.isProcessing) {
        const discoveryAggregators = response.data.aggregators;
        const { existingInstitutionLoginId } = response.data;
        let institutionLoginToUpsert: types.DiscoveryInstitutionLogin | undefined;
        if (existingInstitutionLoginId && existingInstitutionLoginId !== institutionLogin.id) {
          institutionLoginToUpsert = yield select(
            institutionLoginsSelectors.getInstitutionLoginForId,
            existingInstitutionLoginId,
          );
        } else {
          if (!discoveryAggregators) {
            throw new Error('Missing aggregators in account discovery polling response');
          }
          institutionLoginToUpsert = transformers.mergeDiscoveryAggregatorsWithInstitutionLogin(
            discoveryAggregators,
            institutionLogin,
          );
        }

        const allAccountsById = yield select(accountsSelectors.getAccountsById);
        const accountDiscoveryData = transformers.transformResponseToAccountDiscoveryData(
          response.data,
          allAccountsById,
          meta,
        );

        log.debug('Transformed values', institutionLoginToUpsert, accountDiscoveryData);
        yield call(resolve!, { institutionLogin: institutionLoginToUpsert, accountDiscoveryData });

        tracker.track(tracker.events.refreshPoll, {
          tag: 'discoverInstitutionLoginWithAccountsByPolling',
          status: 'success',
        });

        // server is done processing so stop polling
        return;
      }

      timeout -= 1;
      yield delay(refreshPollInterval);
    } catch (error) {
      log.error('Error when discovering accounts for institution-login, error=', error, institutionLogin);
      tracker.track(tracker.events.refreshPoll, {
        tag: 'discoverInstitutionLoginWithAccountsByPolling',
        status: 'error',
      });
      yield call(reject!, error);
      return;
    }
  }

  // only way to get here is with a timeout
  log.error('Timeout when discovering accounts for institution-login', institutionLogin);
  tracker.track(tracker.events.refreshPoll, {
    tag: 'discoverInstitutionLoginWithAccountsByPolling',
    status: 'timeout',
  });
  yield call(reject!, Error('Timeout when discovering accounts for institution-login'));
}

// =================================================================================================
// logPlaidItemEvent
// =================================================================================================
export function* logPlaidItemEvent({
  payload: { event, institution, institutionLogin },
  meta,
}: SagaAction<{
  event: transformers.PlaidItemEvent;
  institution: Institution | null | undefined;
  institutionLogin: types.DiscoveryInstitutionLogin | null | undefined;
}>): SagaIterator {
  log.info('Logging Plaid event', institution, institutionLogin, event);

  const requestUrl = `${requireEnvironmentConfig().services_url}/institution-logins/log-user-activity`;
  const requestBody = transformers.transformPlaidItemActivityToReqestData(event, institution, institutionLogin);

  try {
    yield call(sendRequest, 'POST', requestUrl, requestBody);
    yield put(actions.logPlaidItemEventResponse(null, cpResponseMeta(meta)));
  } catch (error) {
    log.error('Error logging Plaid activity, error=', error);
    yield put(actions.logPlaidItemEventResponse(toResponseError(error), cpResponseMeta(meta)));
  }
}

export function* accountDiscoveryCredentialsFormSubmitWatcher(): SagaIterator {
  yield takeLatestSagaAction<{ institutionLogin: InstitutionLogin }>(
    toActionPattern(actions.accountDiscoveryCredentialsFormSubmit),
    accountDiscovery,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* accountDiscoveryMfaFormWatcher(): SagaIterator {
  yield takeLatestSagaAction<{ mfaResponse: MfaResponse }>(
    toActionPattern(actions.accountDiscoveryMfaFormSubmit),
    submitMfaResponses,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* getPartnerAuthUrisWatcher(): SagaIterator {
  yield takeLatestSagaAction<{
    partnerAuth: { intuitProperty: string; partnerUid: string };
    redirectUri: string;
  }>(toActionPattern(actions.getPartnerAuthUris), getPartnerAuthUris);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* addInstitutionLoginsAndAccountsWatcher(): SagaIterator {
  yield takeLatestSagaAction<{ institutionLogin: InstitutionLogin; accounts: Account[] }>(
    toActionPattern(institutionLoginsActions.addInstitutionLoginAndAccounts),
    addInstitutionLoginAndAccounts,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* getAggregationInfoWatcher(): SagaIterator {
  yield takeLatestSagaAction<types.AggregationInfoRequest>(
    toActionPattern(actions.getAggregationInfo),
    getAggregationInfo,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* discoverInstitutionLoginWithAccountsWatcher(): SagaIterator {
  yield takeLatestSagaAction<{ institutionLogin: types.DiscoveryInstitutionLogin; accounts?: Account[] }>(
    toActionPattern(actions.discoverInstitutionLoginWithAccounts),
    discoverInstitutionLoginWithAccounts,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* discoverInstitutionLoginWithAccountsByPollingWatcher(): SagaIterator {
  yield takeLatestSagaAction<{ institutionLogin: types.DiscoveryInstitutionLogin; pollingRef: string }>(
    toActionPattern(actions.discoverInstitutionLoginWithAccountsByPolling),
    discoverInstitutionLoginWithAccountsByPolling,
  );
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

export function* logPlaidItemActivityWatcher(): SagaIterator {
  yield takeEverySagaAction<{
    event: transformers.PlaidItemEvent;
    institution: Institution | null | undefined;
    institutionLogin: types.DiscoveryInstitutionLogin | null | undefined;
  }>(toActionPattern(actions.logPlaidItemEvent), logPlaidItemEvent);
  // yield take(LOCATION_CHANGE);
  // yield cancel(watcher);
}

// Load all watcher sagas
export default [
  accountDiscoveryCredentialsFormSubmitWatcher,
  accountDiscoveryMfaFormWatcher,
  getPartnerAuthUrisWatcher,
  addInstitutionLoginsAndAccountsWatcher,
  getAggregationInfoWatcher,
  discoverInstitutionLoginWithAccountsWatcher,
  discoverInstitutionLoginWithAccountsByPollingWatcher,
  logPlaidItemActivityWatcher,
];
